import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTimeDE } from '../../../../lib/utils/date';
import { Button } from '../../../../ui/components/ui/button';
import { BillingTermMetaResponseDto } from '../../../products/types/response';
import { OfferDisplayStatus } from '../../constants/offer-display-status.enum';
import { formatCustomerPerson } from '../../lib/customer';
import { canArchiveOffer, canCorrectOffer, canSendOffer, getOfferDisplayStatus } from '../../lib/offer-status';
import { toSavedOfferPreviewData } from '../../lib/preview';
import { OfferMetaResponseDto, OfferResponseDto } from '../../types/response';
import OfferPreviewFrame from '../offer-preview-frame/offer-preview-frame';
import OfferStatusBadge from '../offer-status-badge/offer-status-badge';

export interface OfferDetailProps {
  offer: OfferResponseDto;
  billingTerms: BillingTermMetaResponseDto[];
  meta: OfferMetaResponseDto;
  onSend: () => void;
  onRevoke: () => void;
  onArchive: () => void;
  /** Opens the contract data form of an accepted offer. */
  onCorrect: () => void;
  onViewOfferFile: () => void;
}

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[12px] text-[#888]">{label}</span>
    <span className="text-[14px] font-bold text-[#12282A]">{children}</span>
  </div>
);

/**
 * A sent, accepted or revoked offer, read only – what the customer sees, plus resend and revoke while it is open,
 * correcting while it is accepted and archiving once it is closed. Archived offers have no actions.
 */
const OfferDetail = ({ offer, billingTerms, meta, onSend, onRevoke, onArchive, onCorrect, onViewOfferFile }: OfferDetailProps) => {
  const status = getOfferDisplayStatus(offer);
  const termMonths = billingTerms.find(({ term }) => term === offer.product.term)?.months ?? null;
  const person = formatCustomerPerson(offer.customer);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <h1 className="text-[32px] font-bold leading-[38px] text-[#12282A]">{offer.customer.name ?? offer.customer.mail}</h1>
            <OfferStatusBadge status={status} />
          </div>
          <p className="text-[16px] text-[#888]">{[person, offer.customer.mail].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="flex gap-3">
          {offer.offerFile && (
            <Button variant="outline" onClick={onViewOfferFile}>
              Angebots-PDF ansehen
            </Button>
          )}
          {canArchiveOffer(offer) && (
            <Button variant="outline" onClick={onArchive}>
              Akzeptieren
            </Button>
          )}
          {canCorrectOffer(offer) && (
            <Button variant="outline" onClick={onCorrect}>
              Vertragsdaten korrigieren
            </Button>
          )}
          {canSendOffer(offer) && (
            <>
              {/* A draft was never with the customer – it is deleted from the list, not revoked. */}
              {status !== OfferDisplayStatus.NOT_SENT && (
                <Button variant="outline" onClick={onRevoke}>
                  Zurückziehen
                </Button>
              )}
              <Button onClick={onSend}>{status === OfferDisplayStatus.NOT_SENT ? 'Versenden' : 'Erneut versenden'}</Button>
            </>
          )}
          {status === OfferDisplayStatus.ACCEPTED && offer.acceptance && (
            <Button asChild>
              <Link to={`/organisations/${offer.acceptance.organizationId}`}>Zum Kunden</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-10 gap-y-3 rounded-lg bg-white px-9 py-5">
        <Fact label="Erstellt">{formatDateTimeDE(offer.createdAt)}</Fact>
        <Fact label="Zuletzt versendet">{formatDateTimeDE(offer.sentAt)}</Fact>
        {status === OfferDisplayStatus.ACCEPTED ? (
          <Fact label="Angenommen am">{formatDateTimeDE(offer.acceptance?.acceptedAt)}</Fact>
        ) : status === OfferDisplayStatus.REVOKED ? (
          <Fact label="Zurückgezogen am">{formatDateTimeDE(offer.closedAt)}</Fact>
        ) : (
          <Fact label="Link gültig bis">{formatDateTimeDE(offer.expiresAt)}</Fact>
        )}
        {offer.archivedAt && <Fact label="Archiviert am">{formatDateTimeDE(offer.archivedAt)}</Fact>}
      </div>

      <OfferPreviewFrame preview={toSavedOfferPreviewData(offer, meta)} termMonths={termMonths} />
    </div>
  );
};

export default OfferDetail;
